import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  Close,
  Edit,
  LocalPrintshop,
  Logout,
  Search,
  Visibility,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import { dischargeBaby } from "../../../api/baby";

const getStatusColor = (status) => {
  switch (status) {
    case "registered":
      return "default";
    case "admitted":
      return "primary";
    case "under_observation":
      return "warning";
    case "discharged":
      return "success";
    case "referred":
      return "secondary";
    case "deceased":
      return "error";
    default:
      return "default";
  }
};

export default function BabyList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const token = auth?.user?.token;
  const user = auth?.user?.user;

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState("");
  const [status, setStatus] = useState("");
  const [doctor, setDoctor] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dischargeOpen, setDischargeOpen] = useState(false);
  const [selectedBaby, setSelectedBaby] = useState(null);
  const [dischargeData, setDischargeData] = useState({
    discharge_date: "",
    discharge_time: "",
    discharge_condition: "",
    discharge_notes: "",
    discharged_by: user?.id || "",
  });
  const [submittingDischarge, setSubmittingDischarge] = useState(false);

  const { t } = useTranslation();

  const statusOptions = [
    { value: "", label: t("baby.list.allStatuses", "All Statuses") },
    { value: "registered", label: t("baby.form.statusOptions.registered", "Registered") },
    { value: "admitted", label: t("baby.form.statusOptions.admitted", "Admitted") },
    { value: "under_observation", label: t("baby.form.statusOptions.under_observation", "Under Observation") },
    { value: "discharged", label: t("baby.form.statusOptions.discharged", "Discharged") },
    { value: "referred", label: t("baby.form.statusOptions.referred", "Referred") },
    { value: "deceased", label: t("baby.form.statusOptions.deceased", "Deceased") },
  ];

  const genderOptions = [
    { value: "", label: t("baby.list.allGenders", "All Genders") },
    { value: "1", label: t("baby.list.male", "Male") },
    { value: "2", label: t("baby.list.female", "Female") },
  ];

  const queryString = useMemo(
    () =>
      `babies/?page=${page}&page_size=${rowsPerPage}&search=${encodeURIComponent(search)}&gender=${gender}&current_status=${status}&doctor=${doctor}&start_date=${startDate}&end_date=${endDate}`,
    [page, rowsPerPage, search, gender, status, doctor, startDate, endDate]
  );

  const { data, isLoading, isError } = useFetchObjects(
    ["babies", page, rowsPerPage, search, gender, status, doctor, startDate, endDate],
    queryString,
    token
  );

  const { data: doctorsData } = useFetchObjects(
    ["baby-doctor-filter"],
    "users/?page_size=100&doctor=true",
    token
  );

  const doctorOptions = doctorsData?.results || [];

  const openDischargeDialog = (baby) => {
    setSelectedBaby(baby);
    setDischargeData({
      discharge_date: "",
      discharge_time: "",
      discharge_condition: "",
      discharge_notes: "",
      discharged_by: user?.id || "",
    });
    setDischargeOpen(true);
  };

  const handleDischarge = async () => {
    if (!selectedBaby) return;
    setSubmittingDischarge(true);
    try {
      await dischargeBaby(selectedBaby.id, dischargeData, token);
      queryClient.invalidateQueries({ queryKey: ["babies"] });
      queryClient.invalidateQueries({ queryKey: ["baby", String(selectedBaby.id)] });
      toast.success(t("baby.list.dischargeSuccess", "Baby discharged successfully."));
      setDischargeOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t("baby.list.dischargeError", "Unable to discharge baby."));
    } finally {
      setSubmittingDischarge(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {t("baby.list.title", "Baby List")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {t("baby.list.description", "Search, review, print, and discharge registered babies.")}
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate("/baby/register")}>
            {t("baby.list.registerButton", "Register Baby")}
          </Button>
        </Stack>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={t("baby.list.searchPlaceholder", "Search Baby ID, Baby Name, Mother Name")}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              InputProps={{ endAdornment: <Search color="action" /> }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t("baby.list.gender", "Gender")}</InputLabel>
              <Select label={t("baby.list.gender", "Gender")} value={gender} onChange={(e) => setGender(e.target.value)}>
                {genderOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t("baby.list.status", "Status")}</InputLabel>
              <Select label={t("baby.list.status", "Status")} value={status} onChange={(e) => setStatus(e.target.value)}>
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t("baby.list.doctor", "Doctor")}</InputLabel>
              <Select label={t("baby.list.doctor", "Doctor")} value={doctor} onChange={(e) => setDoctor(e.target.value)}>
                <MenuItem value="">{t("baby.list.allDoctors", "All Doctors")}</MenuItem>
                {doctorOptions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {`${item.first_name || ""} ${item.last_name || ""}`.trim() || item.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1.5}>
            <TextField
              fullWidth
              type="date"
              label={t("baby.list.startDate", "Start Date")}
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <TextField
              fullWidth
              type="date"
              label={t("baby.list.endDate", "End Date")}
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>
        </Grid>

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f7f9fc" }}>
                <TableCell>{t("baby.list.babyId", "Baby ID")}</TableCell>
                <TableCell>{t("baby.list.babyName", "Baby Name")}</TableCell>
                <TableCell>{t("baby.list.motherName", "Mother Name")}</TableCell>
                <TableCell>{t("baby.list.gender", "Gender")}</TableCell>
                <TableCell>{t("baby.list.dateOfBirth", "Date of Birth")}</TableCell>
                <TableCell>{t("baby.list.birthWeight", "Birth Weight")}</TableCell>
                <TableCell>{t("baby.list.deliveryType", "Delivery Type")}</TableCell>
                <TableCell>{t("baby.list.doctor", "Doctor")}</TableCell>
                <TableCell>{t("baby.list.status", "Status")}</TableCell>
                <TableCell align="center">{t("baby.list.actions", "Actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.results?.length ? (
                data.results.map((baby) => (
                  <TableRow key={baby.id} hover>
                    <TableCell>{baby.baby_code}</TableCell>
                    <TableCell>{baby.baby_name}</TableCell>
                    <TableCell>{baby.mother_name}</TableCell>
                    <TableCell>{baby.gender_display}</TableCell>
                    <TableCell>{baby.date_of_birth}</TableCell>
                    <TableCell>{baby.birth_weight}</TableCell>
                    <TableCell>{baby.delivery_type_display}</TableCell>
                    <TableCell>{baby.responsible_doctor_name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={baby.current_status_display}
                        color={getStatusColor(baby.current_status)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => navigate(`/baby/${baby.id}`)}>
                        <Visibility />
                      </IconButton>
                      <IconButton color="secondary" onClick={() => navigate(`/baby/${baby.id}/edit`)}>
                        <Edit />
                      </IconButton>
                      <IconButton color="info" onClick={() => window.open(`/baby/${baby.id}?print=1`, "_blank")}>
                        <LocalPrintshop />
                      </IconButton>
                      <IconButton
                        color="warning"
                        onClick={() => openDischargeDialog(baby)}
                        disabled={baby.current_status === "discharged"}
                      >
                        <Logout />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    {isError ? t("baby.list.loadError", "Unable to load baby records.") : t("baby.list.noRecords", "No baby records found.")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={3} flexWrap="wrap" gap={2}>
          <Typography variant="body2">
            Total Records: <strong>{data?.count || 0}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            <TextField
              select
              size="small"
              label={t("baby.list.rows", "Rows")}
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(1);
              }}
              sx={{ width: 100 }}
            >
              {[10, 25, 50].map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={t("baby.list.page", "Page")}
              type="number"
              value={page}
              onChange={(e) => setPage(Math.max(1, Number(e.target.value) || 1))}
              sx={{ width: 90 }}
            />
          </Stack>
        </Stack>
      </Paper>

      <Dialog open={dischargeOpen} onClose={() => setDischargeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("baby.info.dischargeDialog.title", "Discharge Baby")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              fullWidth
              type="date"
              label={t("baby.info.dischargeDialog.date", "Discharge Date")}
              InputLabelProps={{ shrink: true }}
              value={dischargeData.discharge_date}
              onChange={(e) => setDischargeData((prev) => ({ ...prev, discharge_date: e.target.value }))}
            />
            <TextField
              fullWidth
              type="time"
              label={t("baby.info.dischargeDialog.time", "Discharge Time")}
              InputLabelProps={{ shrink: true }}
              value={dischargeData.discharge_time}
              onChange={(e) => setDischargeData((prev) => ({ ...prev, discharge_time: e.target.value }))}
            />
            <TextField
              fullWidth
              label={t("baby.info.dischargeDialog.condition", "Condition at Discharge")}
              value={dischargeData.discharge_condition}
              onChange={(e) => setDischargeData((prev) => ({ ...prev, discharge_condition: e.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label={t("baby.info.dischargeDialog.notes", "Discharge Notes")}
              value={dischargeData.discharge_notes}
              onChange={(e) => setDischargeData((prev) => ({ ...prev, discharge_notes: e.target.value }))}
            />
            <TextField
              fullWidth
              label={t("baby.info.dischargeDialog.dischargedBy", "Discharged By")}
              value={`${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.username || ""}
              disabled
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<Close />} onClick={() => setDischargeOpen(false)}>
            {t("baby.form.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleDischarge}
            disabled={submittingDischarge}
          >
            {submittingDischarge ? t("baby.form.save", "Saving...") : t("baby.info.dischargeDialog.confirm", "Confirm Discharge")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
